// War-room replay gallery — login-gated index of every published replay.
//
// Reached via `?replays`. Lists curated war-room snapshots as cards (the same
// feel as the desktop Runs feed); clicking one opens its `?replay=<id>` view.
// Read-only, no costs (the published snapshots have them stripped).

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertCircle, Users, ArrowRight, Inbox, FolderOpen } from 'lucide-react';
import { listReplays, ApiError, type ReplaySummary } from '../lib/api';

interface Props {
  /** Open a replay by id (parent swaps to the single-replay view). */
  onOpen(id: string): void;
  /** Leave the gallery for the normal dashboard. */
  onBack?(): void;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ReplayCard({ r, onOpen }: { r: ReplaySummary; onOpen(id: string): void }) {
  return (
    <button
      onClick={() => onOpen(r.id)}
      className="group w-full text-left rounded-lg border border-[#2a2a3a] bg-[#111119] hover:border-[#00FFB2]/40 hover:bg-[#14141d] transition-colors p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wide">
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#00FFB2]/10 text-[#00FFB2] font-semibold">
          War room
        </span>
        {r.coordinator && (
          <span className="text-[#8888a0]">
            coord <span className="text-[#aaaab8] font-medium">{r.coordinator}</span>
          </span>
        )}
        {r.seat_count != null && (
          <span className="inline-flex items-center gap-1 text-[#8888a0]">
            <Users className="w-3 h-3" />
            {r.seat_count} {r.seat_count === 1 ? 'seat' : 'seats'}
          </span>
        )}
        <span className="ml-auto text-[#5a5a6e] normal-case tracking-normal">{fmtDate(r.created_at)}</span>
      </div>

      <h3 className="text-white font-semibold leading-snug group-hover:text-[#00FFB2] transition-colors">
        {r.title ?? r.id}
      </h3>

      {r.summary && (
        <p className="mt-1.5 text-sm text-[#8888a0] leading-relaxed line-clamp-2">{r.summary}</p>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {r.tags.slice(0, 5).map((t) => (
          <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-[#16161e] text-[#8fb3a5] border border-[#2a2a3a]">
            {t}
          </span>
        ))}
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-[#5a5a6e] group-hover:text-[#00FFB2] transition-colors">
          Open replay <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </button>
  );
}

function fmtCount(value: number | null | undefined, noun: string): string {
  if (value == null) return `0 ${noun}s`;
  return `${value.toLocaleString()} ${noun}${value === 1 ? '' : 's'}`;
}

function DatasetCard({ r, onOpen }: { r: ReplaySummary; onOpen(id: string): void }) {
  return (
    <button
      onClick={() => onOpen(r.id)}
      className="group w-full text-left rounded-lg border border-[#2a2a3a] bg-[#101018] hover:border-[#00FFB2]/40 hover:bg-[#14141d] transition-colors p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#2a2a3a] bg-[#16161e] text-[#00FFB2]">
          <FolderOpen className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 text-[11px] uppercase tracking-wide">
            <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#00FFB2]/10 text-[#00FFB2] font-semibold">
              Dataset
            </span>
            <span className="text-[#8888a0]">{fmtCount(r.row_count, 'run')}</span>
          </div>
          <h3 className="text-white font-semibold leading-snug group-hover:text-[#00FFB2] transition-colors">
            {r.title ?? r.id}
          </h3>
          <p className="mt-1 text-sm text-[#8888a0] leading-relaxed">
            Every single-model solve we ran. Open the full dataset and inspect each row.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-[#5a5a6e] group-hover:text-[#00FFB2] transition-colors shrink-0">
          Open <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </button>
  );
}

export default function ReplayGallery({ onOpen, onBack }: Props) {
  const [visibleCount, setVisibleCount] = useState(30);
  const { data: replays, isLoading, error } = useQuery<ReplaySummary[], ApiError>({
    queryKey: ['replays'],
    queryFn: listReplays,
    retry: (n, err) => !(err instanceof ApiError && [401, 403].includes(err.status)) && n < 2,
  });

  const warRoomReplays = (replays ?? []).filter((r) => r.kind !== 'dataset');
  const datasetReplays = (replays ?? []).filter((r) => r.kind === 'dataset');
  const visibleWarRoomReplays = warRoomReplays.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      <header className="sticky top-0 z-10 border-b border-[#2a2a3a] bg-[#0a0a0f]/95 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-sm font-bold text-white tracking-tight">ATO</span>
            <span className="text-[#2a2a3a]">·</span>
            <span className="text-sm text-[#8888a0]">War-room replays</span>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="shrink-0 text-xs text-[#8888a0] hover:text-white transition-colors"
            >
              Dashboard
            </button>
          )}
          <a
            href="https://agentictool.ai"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#00FFB2]/10 border border-[#00FFB2]/30 text-[#00FFB2] text-xs font-medium hover:bg-[#00FFB2]/15 transition-colors"
          >
            Get ATO
          </a>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Replays</h1>
          <p className="text-sm text-[#8888a0] mt-1">
            Real multi-model war rooms, replayable end to end. Every prompt, response, model and token
            count is a receipt. Pick one to watch the models work.
          </p>
        </div>

        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-28 bg-[#111119] rounded-lg border border-[#2a2a3a]" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-400 font-medium">Could not load replays</p>
              <p className="text-xs text-[#8888a0] mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        )}

        {!isLoading && !error && replays && replays.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <Inbox className="w-10 h-10 text-[#5a5a6e]" />
            <p className="text-[#8888a0] text-sm">No replays published yet.</p>
          </div>
        )}

        {!isLoading && !error && replays && replays.length > 0 && (
          <div className="space-y-8">
            {warRoomReplays.length > 0 && (
              <div className="space-y-3">
                {visibleWarRoomReplays.map((r) => (
                  <ReplayCard key={r.id} r={r} onOpen={onOpen} />
                ))}
                {visibleCount < warRoomReplays.length && (
                  <div className="pt-2">
                    <button
                      onClick={() => setVisibleCount((count) => count + 30)}
                      className="inline-flex items-center justify-center rounded-md border border-[#2a2a3a] bg-[#111119] px-4 py-2 text-sm text-[#aaaab8] hover:border-[#00FFB2]/40 hover:text-white transition-colors"
                    >
                      Show more
                    </button>
                  </div>
                )}
              </div>
            )}

            {datasetReplays.length > 0 && (
              <section className="space-y-3">
                <div className="border-t border-[#2a2a3a] pt-4">
                  <h2 className="text-sm font-semibold text-white">Full run data</h2>
                  <p className="mt-1 text-sm text-[#8888a0]">
                    Every single-model solve we ran. Nothing hidden.
                  </p>
                </div>
                <div className="space-y-3">
                  {datasetReplays.map((r) => (
                    <DatasetCard key={r.id} r={r} onOpen={onOpen} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-[#16161e] py-5 px-6 mt-auto">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5a5a6e]">
          <p>
            Powered by{' '}
            <a href="https://agentictool.ai" target="_blank" rel="noreferrer" className="text-[#00FFB2] hover:underline">
              ATO
            </a>{' '}
            — these are real, replayable AI receipts.
          </p>
          <a href="https://agentictool.ai" target="_blank" rel="noreferrer" className="hover:text-[#aaaab8] transition-colors">
            agentictool.ai
          </a>
        </div>
      </footer>
    </div>
  );
}
